import { CalculosInventario, PartilhaResultado } from 'src/drheranca/types';
import {
  computeLiquidoFinalPartilha,
  resolveEmolumentosDevidos,
} from 'src/drheranca/services/partilhaTotalLiquidoBreakdown';
import {
  PARTILHA_MATRIX_ROW_DEFS,
  partilhaRowExcelDescription,
} from '../definitions/partilhaMatrixRows';
import { ExcelCellValue, ExcelSheetData } from '../types';

const EPS = 0.01;

export function shouldShowPartilhaRow(
  row: (typeof PARTILHA_MATRIX_ROW_DEFS)[number],
  partilhas: PartilhaResultado[],
): boolean {
  if (!row.hideIfEmpty) return true;
  const values = partilhas.map((p) => row.getValue(p));
  const total = values.reduce((s, v) => s + v, 0);
  return values.some((v) => Math.abs(v) > EPS) || Math.abs(total) > EPS;
}

export interface PartilhaMatrixFooterRowDef {
  key: string;
  label: string;
  excelDescription: string;
  isDeduction: boolean;
  getValues: (partilhas: PartilhaResultado[]) => number[];
  getTotal: (partilhas: PartilhaResultado[], values: number[]) => number;
}

function footerRows(
  calculos: CalculosInventario,
  custosTributosPorPessoa: Record<
    string,
    { registroImoveis: number; honorariosAdvocaticios: number }
  >,
): PartilhaMatrixFooterRowDef[] {
  const partilhas = calculos.partilhaPorHerdeiro || [];
  if (!partilhas.some((p) => (p.valorRecebidoBruto ?? 0) > EPS)) return [];

  const rows: PartilhaMatrixFooterRowDef[] = [
    {
      key: 'emolumentos',
      label: 'Emolumentos cartório (−)',
      excelDescription: 'Rateio dos emolumentos de Notas (cartório)',
      isDeduction: true,
      getValues: (ps) => ps.map((p) => resolveEmolumentosDevidos(p, calculos, ps)),
      getTotal: (ps, values) =>
        values.reduce((s, v) => s + v, 0) || calculos.emolumentosTotal || 0,
    },
    {
      key: 'registro',
      label: 'Registro de Imóveis (−)',
      excelDescription: 'Rateio da estimativa de RI e pré-notação',
      isDeduction: true,
      getValues: (ps) =>
        ps.map(
          (p) => custosTributosPorPessoa[String(p.herdeiroId)]?.registroImoveis ?? 0,
        ),
      getTotal: (_ps, values) =>
        calculos.registroImoveisTotal ?? values.reduce((s, v) => s + v, 0),
    },
    {
      key: 'honorarios',
      label: 'Honorários advocatícios (−)',
      excelDescription: 'Rateio da estimativa contratual (referência 8%)',
      isDeduction: true,
      getValues: (ps) =>
        ps.map(
          (p) =>
            custosTributosPorPessoa[String(p.herdeiroId)]?.honorariosAdvocaticios ?? 0,
        ),
      getTotal: (_ps, values) => values.reduce((s, v) => s + v, 0),
    },
  ];

  rows.push({
    key: 'liquido',
    label: 'Total a receber (líquido)',
    excelDescription: 'Subtotal menos todas as deduções',
    isDeduction: false,
    getValues: (ps) =>
      ps.map((p) =>
        computeLiquidoFinalPartilha(p, calculos, ps, custosTributosPorPessoa),
      ),
    getTotal: (_ps, values) => values.reduce((s, v) => s + v, 0),
  });

  return rows;
}

export function buildPartilhaMatrixExcelSheet(
  calculos: CalculosInventario,
  custosTributosPorPessoa: Record<
    string,
    { registroImoveis: number; honorariosAdvocaticios: number }
  >,
): ExcelSheetData {
  const partilhas = calculos.partilhaPorHerdeiro || [];
  const header: ExcelCellValue[] = [
    'Cálculo / Pessoa',
    'Descrição',
    ...partilhas.map((p) => p.herdeiroNome || '—'),
    'Total',
  ];
  const rows: ExcelCellValue[][] = [header];

  PARTILHA_MATRIX_ROW_DEFS.forEach((row) => {
    if (!shouldShowPartilhaRow(row, partilhas)) return;
    const values = partilhas.map((p) => row.getValue(p));
    const total = values.reduce((s, v) => s + v, 0);
    const signedValues = values.map((v) => (row.isDeduction && v > 0 ? -v : v));
    const signedTotal = row.isDeduction && total > 0 ? -total : total;
    rows.push([
      row.label,
      partilhaRowExcelDescription(row),
      ...signedValues,
      signedTotal,
    ]);
  });

  footerRows(calculos, custosTributosPorPessoa).forEach((row) => {
    const values = row.getValues(partilhas);
    const total = row.getTotal(partilhas, values);
    if (row.key === 'liquido') {
      rows.push([row.label, row.excelDescription, ...values, total]);
      return;
    }
    rows.push([
      row.label,
      row.excelDescription,
      ...values.map((v) => -v),
      -total,
    ]);
  });

  return {
    rows,
    colWidths: [34, 50, ...partilhas.map(() => 20), 20],
    valueColumnStart: 2,
  };
}

export { PARTILHA_MATRIX_ROW_DEFS, partilhaRowExcelDescription };
export function getPartilhaMatrixFooterRows(
  calculos: CalculosInventario,
  custosTributosPorPessoa: Record<
    string,
    { registroImoveis: number; honorariosAdvocaticios: number }
  >,
) {
  return footerRows(calculos, custosTributosPorPessoa);
}
